import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Add, Box1, ShoppingCart, TickCircle, Trash } from "iconsax-react";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ProductIconTile, StatusBadge } from "@/components/Atoms";
import {
  CopyLinkRow,
  FilterChip,
  QuantityStepper,
  SearchField,
  SefanoButton,
} from "@/components/Molecules";
import { formatPersianNumber } from "@/lib/format/persian";
import type { Product } from "@/api/schemas/product";
import type { QuickOrderPanelProps } from "./QuickOrderPanel.types";
import * as styles from "./QuickOrderPanel.styles";

export type { QuickOrderPanelProps, QuickOrderCategory } from "./QuickOrderPanel.types";

export const QuickOrderPanel = memo(function QuickOrderPanel({
  searchQuery,
  onSearchChange,
  filteredProducts,
  cartItems,
  totalPrice,
  orderLink,
  orderError,
  isProductsLoading = false,
  isCreatingOrder = false,
  categories = [],
  activeCategoryId = "all",
  onCategoryChange,
  onAddProduct,
  onRemoveProduct,
  onQuantityChange,
  onCreateOrderLink,
  onEditCart,
  onReset,
}: QuickOrderPanelProps) {
  const { t } = useTranslation("orders");
  const theme = useTheme();

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(event.target.value);
    },
    [onSearchChange],
  );

  const cartQtyMap = useMemo(
    () => new Map(cartItems.map((item) => [item.product.id, item.quantity])),
    [cartItems],
  );

  const itemCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems],
  );

  const cartEmpty = cartItems.length === 0;

  const handleQuantityChange = useCallback(
    (product: Product, quantity: number) => {
      if (quantity <= 0) {
        onRemoveProduct(product.id);
        return;
      }
      const current = cartQtyMap.get(product.id) ?? 0;
      if (current === 0) {
        onAddProduct(product);
        if (quantity > 1) {
          onQuantityChange(product.id, quantity);
        }
        return;
      }
      onQuantityChange(product.id, quantity);
    },
    [cartQtyMap, onAddProduct, onQuantityChange, onRemoveProduct],
  );

  const renderCartSection = (showCta: boolean) => (
    <Box sx={styles.cartCard}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={styles.sectionHeader}>
          <ShoppingCart size={19} variant="Bold" color={theme.palette.secondary.main} />
          <Typography sx={styles.sectionTitle}>{t("create.cartTitle")}</Typography>
        </Box>
        {!cartEmpty ? (
          <Typography sx={styles.cartBadge}>
            {t("create.itemCount", { count: formatPersianNumber(itemCount) })}
          </Typography>
        ) : null}
      </Box>

      {cartEmpty ? (
        <Box sx={styles.emptyCart}>
          <Box1 size={34} variant="Linear" color={theme.palette.text.disabled} />
          <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.78125rem" }}>
            {t("create.emptyCart")}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.7, maxWidth: 260 }}>
            {t("create.emptyCartHint")}
          </Typography>
        </Box>
      ) : (
        <Box sx={styles.cartList}>
          {cartItems.map((item) => (
            <Box key={item.product.id} sx={styles.cartItem}>
              <Box sx={styles.cartItemTop}>
                <ProductIconTile imageUrl={item.product.image_url} alt={item.product.name} />
                <Box sx={styles.cartItemInfo}>
                  <Typography sx={styles.cartItemName}>{item.product.name}</Typography>
                  <Typography sx={styles.cartItemUnit}>
                    {formatPersianNumber(item.product.price)} {t("create.currencyPerUnit")}
                  </Typography>
                </Box>
                <Typography sx={styles.cartItemTotal}>
                  {formatPersianNumber(item.product.price * item.quantity)}
                </Typography>
              </Box>
              <Box sx={styles.cartItemBottom}>
                <QuantityStepper
                  value={item.quantity}
                  min={0}
                  max={item.product.stock}
                  onChange={(qty) => handleQuantityChange(item.product, qty)}
                />
                <Box
                  component="button"
                  type="button"
                  sx={styles.removeButton}
                  onClick={() => onRemoveProduct(item.product.id)}
                  aria-label={t("create.removeFromCart")}
                >
                  <Trash size={16} variant="Linear" color={theme.palette.error.main} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={styles.summaryBlock}>
        <Box sx={styles.summaryRow}>
          <Typography sx={styles.summaryLabel}>{t("create.itemsTotal")}</Typography>
          <Typography sx={styles.summaryValue}>{formatPersianNumber(itemCount)}</Typography>
        </Box>
        <Box sx={styles.summaryRow}>
          <Typography sx={styles.summaryLabel}>{t("create.grandTotal")}</Typography>
          <Typography sx={styles.totalValue}>
            {formatPersianNumber(totalPrice)}{" "}
            <Typography component="span" sx={styles.totalCurrency}>
              {t("create.currency")}
            </Typography>
          </Typography>
        </Box>
      </Box>

      {!orderLink && showCta ? (
        <>
          <SefanoButton
            intent="primary"
            fullWidth
            disabled={cartEmpty || isCreatingOrder}
            onClick={onCreateOrderLink}
          >
            {isCreatingOrder ? t("create.creatingLink") : t("create.generateLink")}
          </SefanoButton>
          {orderError ? <Typography role="alert" sx={styles.ctaError}>{orderError}</Typography> : null}
          {cartEmpty ? <Typography sx={styles.ctaHint}>{t("create.ctaHint")}</Typography> : null}
        </>
      ) : null}

      {orderLink ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={styles.successBanner}>
            <TickCircle size={19} variant="Bold" color={theme.palette.success.main} />
            <Typography sx={styles.successText}>{t("create.linkCreated")}</Typography>
          </Box>
          <CopyLinkRow url={orderLink} size="sm" copyLabel={t("detail.copyLink")} />
          <Typography sx={styles.linkHint}>{t("create.linkHint")}</Typography>
          <Box sx={styles.ghostActions}>
            <Box
              component="button"
              type="button"
              sx={styles.ghostButton}
              onClick={() => onEditCart?.()}
            >
              {t("create.editCart")}
            </Box>
            <Box
              component="button"
              type="button"
              sx={{ ...styles.ghostButton, color: "text.secondary" }}
              onClick={() => onReset?.()}
            >
              {t("create.newOrder")}
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <>
      <Box sx={styles.layout}>
        <Box sx={styles.panelCard}>
          <Box sx={styles.sectionHeader}>
            <Box1 size={19} variant="Bold" color={theme.palette.primary.main} />
            <Typography sx={styles.sectionTitle}>{t("create.pickerTitle")}</Typography>
          </Box>

          <SearchField
            placeholder={t("create.searchPlaceholder")}
            value={searchQuery}
            onChange={handleSearchChange}
            fieldSize="compact"
          />

          {categories.length > 0 ? (
            <Box sx={styles.categoryRow}>
              {categories.map((category) => (
                <FilterChip
                  key={String(category.id)}
                  label={category.name}
                  active={activeCategoryId === category.id}
                  onClick={() => onCategoryChange?.(category.id)}
                />
              ))}
            </Box>
          ) : null}

          {isProductsLoading ? (
            <Box sx={styles.loadingWrap}>
              <CircularProgress size={32} />
            </Box>
          ) : filteredProducts.length === 0 ? (
            <Box sx={styles.emptyPicker}>
              <Box1 size={34} variant="Linear" color={theme.palette.text.disabled} />
              <Typography variant="body2" color="text.secondary">
                {t("create.noProducts")}
              </Typography>
            </Box>
          ) : (
            <Box sx={styles.productList}>
              {filteredProducts.map((product) => {
                const qty = cartQtyMap.get(product.id) ?? 0;
                const outOfStock = product.stock <= 0;
                return (
                  <Box key={product.id} sx={styles.productRow(outOfStock)}>
                    <ProductIconTile imageUrl={product.image_url} alt={product.name} />
                    <Box sx={styles.productInfo}>
                      <Typography sx={styles.productName}>{product.name}</Typography>
                      <Typography sx={styles.productMeta}>
                        {formatPersianNumber(product.price)} {t("create.currency")} ·{" "}
                        {t("create.stockLabel", { count: formatPersianNumber(product.stock) })}
                      </Typography>
                    </Box>
                    {outOfStock ? (
                      <StatusBadge color="error" size="sm" label={t("create.outOfStock")} />
                    ) : qty > 0 ? (
                      <QuantityStepper
                        value={qty}
                        min={0}
                        max={product.stock}
                        onChange={(value) => handleQuantityChange(product, value)}
                      />
                    ) : (
                      <>
                        <Box sx={{ display: { xs: "none", md: "block" } }}>
                          <QuantityStepper
                            value={0}
                            min={0}
                            max={product.stock}
                            onChange={(value) => handleQuantityChange(product, value)}
                          />
                        </Box>
                        <Box
                          component="button"
                          type="button"
                          sx={styles.addButton}
                          onClick={() => onAddProduct(product)}
                          aria-label={t("create.addToCart")}
                        >
                          <Add size={22} variant="Linear" color="#fff" />
                        </Box>
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Box sx={{ display: { xs: "none", lg: "block" } }}>{renderCartSection(true)}</Box>
        <Box sx={{ display: { xs: "block", lg: "none" } }}>{renderCartSection(false)}</Box>
      </Box>

      {!orderLink ? (
        <Box sx={styles.mobileStickyCta}>
          <SefanoButton
            intent="primary"
            fullWidth
            disabled={cartEmpty || isCreatingOrder}
            onClick={onCreateOrderLink}
          >
            {isCreatingOrder ? t("create.creatingLink") : t("create.generateLink")}
          </SefanoButton>
          {orderError ? <Typography role="alert" sx={styles.ctaError}>{orderError}</Typography> : null}
          {cartEmpty ? <Typography sx={styles.ctaHint}>{t("create.ctaHint")}</Typography> : null}
        </Box>
      ) : null}

      <Box sx={styles.pageBottomSpacer} />
    </>
  );
});
